import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  assignSubscriptionToOrganization,
} from '../../slice/organizationsSlice';
import { authHeader } from '../../utils/authHeaders';
import { fetchAllSubscriptions } from '../../slice/subscriptionSlice';
import { useToast } from '../../components/Toast';
import DeleteModal from '../../components/atomic/DeleteModal';
import { BASE_URL } from '../../components/constants';
import SharePointConnectionModal from './components/SharePointConnectionModal';
import { fetchSharePointConnection } from '../../slice/sharepointSlice';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/LanguageContext';
import { Search, Plus, Settings2, MoreHorizontal, X, Check, Upload, Pencil, Trash2, Loader2 } from 'lucide-react';

const OrganizationModal = ({ isOpen, onClose, onConfirm, initial = {} }) => {
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    organization_name: '',
    organization_description: '',
    primary_color: '#FFD700',
    logo_blob_path: '',
    footer_address: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempOrganizationId, setTempOrganizationId] = useState(null);

  useEffect(() => {
    if (initial && initial.organization_id) {
      setForm({
        organization_name: initial.organization_name || '',
        organization_description: initial.organization_description || '',
        primary_color: initial.primary_color || '#FFD700',
        logo_blob_path: initial.logo_blob_path || '',
        footer_address: initial.footer_address || '',
      });
      setTempOrganizationId(null); // Use existing organization ID for updates
    } else {
      setForm({ organization_name: '', organization_description: '', primary_color: '#FFD700', logo_blob_path: '', footer_address: '' });
      // Generate a temporary UUID for new organizations
      setTempOrganizationId(crypto.randomUUID());
    }
    setLogoFile(null);
  }, [initial, isOpen]);

  if (!isOpen) return null;

  const handleLogoUpload = async () => {
    if (!logoFile) return form.logo_blob_path;
    
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      // Use temp organization ID for new organizations, or existing ID for updates
      const orgId = initial.organization_id || tempOrganizationId;
      formData.append('organization_id', orgId);
      
      const response = await fetch(`${BASE_URL}/organizations/upload`, {
        method: 'POST',
        headers: { ...authHeader() },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.blob_path;
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
    } finally {
      setIsUploading(false);
    }
    return form.logo_blob_path;
  };

  const handleSubmit = async () => {
    if (!form.organization_name.trim()) return;
    if (isSaving) return;

    setIsSaving(true);
    try {
      const logoPath = await handleLogoUpload();
      // For new organizations, include the temp organization ID so it can be used for creation
      const submitData = { ...form, logo_blob_path: logoPath };
      if (tempOrganizationId) {
        submitData.organization_id = tempOrganizationId;
      }
      await onConfirm(submitData);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = async () => {
    // Clean up any uploaded logo if the modal is closed without saving
    if (logoFile && tempOrganizationId && !initial.organization_id) {
      try {
        // Call cleanup endpoint to remove orphaned logo file
        await fetch(`${BASE_URL}/organizations/cleanup-logo/${tempOrganizationId}`, {
          method: 'DELETE',
          headers: { ...authHeader() },
        });
        console.log('Cleaned up temp logo for organization:', tempOrganizationId);
      } catch (error) {
        console.error('Failed to cleanup temp logo:', error);
      }
    }
    onClose();
  };

  // Predefined color palette (2 rows × 11 colors, matches figma)
  const PRESET_COLORS = [
    '#DC2626', '#E11D74', '#9333EA', '#6D28D9', '#3730A3', '#2563EB', '#0EA5E9', '#06B6D4', '#0D9488', '#065F46', '#16A34A',
    '#65A30D', '#A3A80F', '#EA580C', '#D9480C', '#78350F', '#6B7280', '#475569',
  ];

  // Include the current color as an extra swatch if it's not in the preset list
  // so editing an existing org with a custom color still shows it as selected.
  const currentColor = (form.primary_color || '').trim();
  const currentColorLower = currentColor.toLowerCase();
  const colorsToShow = currentColor && !PRESET_COLORS.some(c => c.toLowerCase() === currentColorLower)
    ? [currentColor, ...PRESET_COLORS]
    : PRESET_COLORS;

  const logoPreviewSrc = logoFile
    ? URL.createObjectURL(logoFile)
    : (form.logo_blob_path && initial.organization_id
        ? `${BASE_URL}/organizations/media/${initial.organization_id}`
        : null);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={handleClose}>
      <div
        className="bg-white rounded-2xl w-[640px] max-h-[90vh] shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB] rounded-t-2xl">
          <h2 className="text-[20px] font-bold text-[#111827]">
            {initial.organization_id ? t('organization.modalEditTitle') : t('organization.modalCreateTitle')}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors"
          >
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          {/* Organisation Logo */}
          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('organization.logoLabel')}</label>
            <div className="flex items-center gap-4">
              <div className="h-14 min-w-[56px] flex items-center justify-center rounded-lg border border-[#E5E7EB] px-3 bg-white">
                {logoPreviewSrc ? (
                  <img src={logoPreviewSrc} alt={t('organization.logoAlt')} className="max-h-10 max-w-[120px] object-contain" />
                ) : (
                  <span className="text-[12px] text-[#9CA3AF]">{t('organization.logoNone')}</span>
                )}
              </div>
              <label
                className="inline-flex items-center gap-2 px-5 py-2.5 text-[14px] font-semibold text-white rounded-lg cursor-pointer hover:opacity-90 transition-colors"
                style={{ backgroundColor: aispaceColor }}
              >
                <Upload size={16} />
                <span>{t('organization.logoUpload')}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files[0])}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Organisation Name */}
          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('organization.nameLabel')}</label>
            <input
              type="text"
              className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF]"
              value={form.organization_name}
              onChange={e => setForm({ ...form, organization_name: e.target.value })}
              placeholder={t('organization.namePlaceholder')}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('organization.descriptionLabel')}</label>
            <textarea
              className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF] min-h-[120px] resize-none"
              value={form.organization_description}
              onChange={e => setForm({ ...form, organization_description: e.target.value })}
              placeholder={t('organization.descriptionPlaceholder')}
            />
          </div>

          {/* Footer address — shown in this org's generated Word documents'
              footer, one line per address element (company/street/phone/
              email/website). Empty by default: no address block, never
              another org's. */}
          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('organization.footerAddressLabel')}</label>
            <textarea
              className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF] min-h-[100px] resize-none"
              value={form.footer_address}
              onChange={e => setForm({ ...form, footer_address: e.target.value })}
              placeholder={t('organization.footerAddressPlaceholder')}
            />
            <p className="mt-1.5 text-[12px] text-[#9CA3AF]">{t('organization.footerAddressHelp')}</p>
          </div>

          {/* Primary Color */}
          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-3">{t('organization.primaryColorLabel')}</label>
            <div className="flex flex-wrap gap-3">
              {colorsToShow.map((color) => {
                const selected = (form.primary_color || '').toLowerCase() === color.toLowerCase()
                return (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setForm({ ...form, primary_color: color })}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-105"
                    style={{
                      backgroundColor: color,
                      outline: selected ? `2px solid ${color}` : 'none',
                      outlineOffset: selected ? '2px' : '0',
                    }}
                    title={color}
                  >
                    {selected && <Check size={18} className="text-white" strokeWidth={3} />}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-2xl">
          <button
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleClose}
            disabled={isSaving || isUploading}
          >
            {t('common.cancel')}
          </button>
          <button
            className="flex items-center justify-center gap-2 px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[170px]"
            style={{ backgroundColor: aispaceColor }}
            onClick={handleSubmit}
            disabled={isUploading || isSaving || !form.organization_name.trim()}
          >
            {(isUploading || isSaving) ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>{isUploading ? t('organization.uploadingState') : t('organization.savingState')}</span>
              </>
            ) : (
              <span>{initial.organization_id ? t('organization.submitSave') : t('organization.submitCreate')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const Organization = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();
  const { organizations, loading, error } = useSelector(state => state.organizations);
  const { subscriptions } = useSelector(state => state.subscriptions);
  const { data: userData } = useSelector(state => state.admin);
  const isSuperAdmin = userData?.user_role === 'SUPER_ADMIN';
  const isAdmin = userData?.user_role === 'ADMIN';
  const adminOrgId = userData?.organization_id;
  const [modalOpen, setModalOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [deleteOrg, setDeleteOrg] = useState(null);
  const [assignSubscriptionModal, setAssignSubscriptionModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [spModalOrg, setSpModalOrg] = useState(null);
  const [spStatuses, setSpStatuses] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdown, setOpenDropdown] = useState(null);
  const itemsPerPage = 5;
  const { addToast, ToastContainer } = useToast();

  useEffect(() => {
    dispatch(fetchAllOrganizations());
    dispatch(fetchAllSubscriptions());
  }, [dispatch]);

  // Load SharePoint connection status for each org
  useEffect(() => {
    if (organizations.length > 0) {
      organizations.forEach(async (org) => {
        try {
          const result = await dispatch(fetchSharePointConnection(org.organization_id)).unwrap();
          setSpStatuses(prev => ({
            ...prev,
            [org.organization_id]: result?.connection_status || null,
          }));
        } catch {
          setSpStatuses(prev => ({ ...prev, [org.organization_id]: null }));
        }
      });
    }
  }, [organizations, dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleAdd = () => {
    setEditOrg(null);
    setModalOpen(true);
  };

  const handleEdit = org => {
    setEditOrg(org);
    setModalOpen(true);
    setOpenDropdown(null);
  };

  const handleDelete = org => {
    setDeleteOrg(org);
    setOpenDropdown(null);
  };

  const handleModalConfirm = async (form) => {
    try {
      if (editOrg) {
        await dispatch(updateOrganization({ ...form, organization_id: editOrg.organization_id })).unwrap();
        addToast(t('organization.toastUpdated'), 'success');
      } else {
        await dispatch(createOrganization(form)).unwrap();
        addToast(t('organization.toastCreated'), 'success');
      }
      setModalOpen(false);
      dispatch(fetchAllOrganizations());
    } catch (err) {
      // Clean up the uploaded logo if organization creation failed
      if (form.organization_id && !editOrg) {
        try {
          await fetch(`${BASE_URL}/organizations/cleanup-logo/${form.organization_id}`, {
            method: 'DELETE',
            headers: { ...authHeader() },
          });
          console.log('Cleaned up logo after failed organization creation:', form.organization_id);
        } catch (cleanupError) {
          console.error('Failed to cleanup logo after failed creation:', cleanupError);
        }
      }
      addToast(t('organization.toastSaveError'), 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteOrganization(deleteOrg.organization_id)).unwrap();
      addToast(t('organization.toastDeleted'), 'success');
      setDeleteOrg(null);
      dispatch(fetchAllOrganizations());
    } catch (err) {
      addToast(t('organization.toastDeleteError'), 'error');
    }
  };

  const handleAssignSubscription = (org) => {
    setSelectedOrg(org);
    setAssignSubscriptionModal(true);
    setOpenDropdown(null);
  };

  const handleSubscriptionAssign = async (subscription_id) => {
    try {
      await dispatch(assignSubscriptionToOrganization({
        organization_id: selectedOrg.organization_id,
        subscription_id: subscription_id
      })).unwrap();
      addToast(t('organization.toastSubAssigned'), 'success');
      setAssignSubscriptionModal(false);
      setSelectedOrg(null);
      dispatch(fetchAllOrganizations());
    } catch (err) {
      addToast(t('organization.toastSubAssignError'), 'error');
    }
  };

  // Filter organizations: ADMIN sees only their own org
  const filteredOrganizations = organizations.filter(org => {
    if (isAdmin && !isSuperAdmin) {
      if (org.organization_id !== adminOrgId) return false;
    }
    return org.organization_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrganizations = filteredOrganizations.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div>
      <ToastContainer />

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        {/* Search and Add Button */}
        <div className="flex items-center justify-between gap-4 px-8 py-4">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder={t('organization.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none transition-colors"
              onFocus={(e) => (e.target.style.borderColor = aispaceColor)}
              onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
            />
          </div>
          {isSuperAdmin && (
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors"
              style={{ backgroundColor: aispaceColor }}
            >
              <Plus size={18} />
              {t('organization.addButton')}
            </button>
          )}
        </div>

        {/* Table Header */}
        <div className="flex items-center px-8 py-3 border-t border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('organization.columnLogo')}</div>
          <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('organization.columnName')}</div>
          <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('organization.columnDescription')}</div>
          <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('organization.columnSharePoint')}</div>
          <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('organization.columnColor')}</div>
          <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('organization.columnSubscription')}</div>
          <div className="w-[150px] flex-shrink-0"></div>
        </div>

        {error && (
          <div className="px-8 py-4 text-[14px] text-red-600 bg-red-50 border-b border-red-100">{error}</div>
        )}

        {/* Table Body */}
        <div>
          {paginatedOrganizations.map((org, index) => {
            const isLastItem = paginatedOrganizations.length > 3 && index === paginatedOrganizations.length - 1;
            const isDropdownOpen = openDropdown === org.organization_id;
            const hasSubscription = org.subscription_id;

            return (
              <div
                key={org.organization_id}
                className={`flex items-center px-8 py-5 hover:bg-[#FAFAFA] transition-colors ${
                  index !== paginatedOrganizations.length - 1 ? 'border-b border-[#E5E7EB]' : ''
                }`}
              >
                {/* Logo */}
                <div className="flex-1">
                  {org.logo_blob_path ? (
                    <img
                      src={`${BASE_URL}/organizations/media/${org.organization_id}`}
                      alt={t('organization.logoAlt')}
                      className="w-10 h-10 object-contain rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-[#F3F4F6] rounded flex items-center justify-center">
                      <span className="text-[10px] text-[#9CA3AF]">{t('organization.logoFallback')}</span>
                    </div>
                  )}
                </div>

                {/* Organization Name */}
                <div className="flex-1 text-[14px] font-medium text-[#111827] truncate pr-4">
                  {org.organization_name}
                </div>

                {/* Description */}
                <div className="flex-1 text-[14px] text-[#6B7280] truncate pr-4">
                  {org.organization_description || '-'}
                </div>

                {/* SharePoint Status */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${
                      spStatuses[org.organization_id] === 'connected'
                        ? 'bg-green-500'
                        : spStatuses[org.organization_id] === 'error'
                          ? 'bg-red-500'
                          : 'bg-gray-300'
                    }`} />
                    <span className="text-[14px] text-[#6B7280]">
                      {spStatuses[org.organization_id] === 'connected' ? t('organization.spConnected') :
                       spStatuses[org.organization_id] === 'error' ? t('organization.spError') : t('organization.spNotConnected')}
                    </span>
                  </div>
                </div>

                {/* Color */}
                <div className="flex-1">
                  <div
                    className="w-8 h-8 rounded-full"
                    style={{ backgroundColor: org.primary_color || '#E5E7EB' }}
                  />
                </div>

                {/* Subscription */}
                <div className="flex-1">
                  {hasSubscription ? (
                    <span className="px-3 py-1 text-[12px] font-medium text-white bg-[#22C55E] rounded-full">
                      {t('organization.subscriptionActive')}
                    </span>
                  ) : (
                    <span className="text-[14px] text-[#6B7280]">{t('organization.subscriptionInactive')}</span>
                  )}
                </div>

                {/* Actions */}
                {(isSuperAdmin || isAdmin) && (
                  <div className="w-[150px] flex-shrink-0 flex items-center justify-end gap-2 relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/settings/manage-organization/${org.organization_id}`);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                    >
                      <Settings2 size={15} />
                      {t('organization.actionManage')}
                    </button>
                    {isSuperAdmin && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(isDropdownOpen ? null : org.organization_id);
                          }}
                          className="p-1.5 text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                        >
                          <MoreHorizontal size={18} />
                        </button>
                        {isDropdownOpen && (
                          <div className={`absolute right-0 ${isLastItem ? 'bottom-full mb-2' : 'top-full mt-2'} w-[230px] bg-white border border-[#E5E7EB] rounded-xl shadow-xl z-[999] p-1.5`}>
                            <button
                              onClick={() => handleEdit(org)}
                              className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors"
                            >
                              <Pencil size={18} className="text-[#374151]" />
                              <span>{t('organization.actionEdit')}</span>
                            </button>
                            <button
                              onClick={() => handleDelete(org)}
                              className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg text-left text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                            >
                              <Trash2 size={18} className="text-[#EF4444]" />
                              <span>{t('organization.actionDelete')}</span>
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-8 py-4 border-t border-[#E5E7EB]">
          <span className="text-[14px] text-[#6B7280]">
            {t('organization.pageOf', { current: currentPage, total: totalPages || 1 })}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('organization.paginationPrev')}
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('organization.paginationNext')}
            </button>
          </div>
        </div>
      </div>
      <OrganizationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
        initial={editOrg || {}}
      />
      <DeleteModal
        isOpen={!!deleteOrg}
        onClose={() => setDeleteOrg(null)}
        onDelete={handleDeleteConfirm}
        title={t('organization.deleteTitle')}
        message={t('organization.deleteMessage', { name: deleteOrg?.organization_name })}
      />
      
      {/* Subscription Assignment Modal */}
      {assignSubscriptionModal && selectedOrg && (
        <div className="fixed inset-0 bg-[#D6D6D6B2] bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-semibold mb-4">
              {t('organization.subModalTitle', { name: selectedOrg.organization_name })}
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('organization.subModalSelectLabel')}</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
                onChange={(e) => {
                  if (e.target.value === 'none') {
                    handleSubscriptionAssign(null);
                  } else {
                    handleSubscriptionAssign(e.target.value);
                  }
                }}
                defaultValue=""
              >
                <option value="" disabled>{t('organization.subModalSelectPlaceholder')}</option>
                <option value="none">{t('organization.subModalNone')}</option>
                {subscriptions.map(sub => (
                  <option key={sub.subscription_id} value={sub.subscription_id}>
                    {t('organization.subModalOptionInfo', { name: sub.subscription_name, tasks: sub.max_tasks, pages: sub.max_pages })}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                onClick={() => {
                  setAssignSubscriptionModal(false);
                  setSelectedOrg(null);
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Connection Modal */}
      <SharePointConnectionModal
        isOpen={!!spModalOrg}
        onClose={() => {
          setSpModalOrg(null);
          // Refresh statuses after modal closes
          organizations.forEach(async (org) => {
            try {
              const result = await dispatch(fetchSharePointConnection(org.organization_id)).unwrap();
              setSpStatuses(prev => ({
                ...prev,
                [org.organization_id]: result?.connection_status || null,
              }));
            } catch {
              setSpStatuses(prev => ({ ...prev, [org.organization_id]: null }));
            }
          });
        }}
        organizationId={spModalOrg?.organization_id}
        organizationName={spModalOrg?.organization_name}
      />
    </div>
  );
};

export default Organization;